import styled from 'styled-components';
import SimpleInfoTable, { SimpleItem } from '../SimpleInfoTable';
import { useEffect, useState } from 'react';
import { SearchModal, SearchResult } from '../modal/SearchModal';
import CaseSearchMap from './CaseSearchMap';
import { useReportContext } from '../../../../context/DetailContext';
import axios from 'axios';
import { s } from '../../components/PeriodStatsPage.styles';
interface CaseSearchProps {
  onResult?: (value: SearchResult | null) => void;
  isPremium?: boolean;
}

const apiHost = process.env.REACT_APP_HOST_API;

const CaseSearch = ({ onResult, isPremium = false }: CaseSearchProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [search, setSearch] = useState<SearchResult | null>(null);
  const [landItems, setLandItems] = useState<SimpleItem[]>([]);
  const [buildingItems, setBuildingItems] = useState<SimpleItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<'land' | 'building'>('land');

  const { setLat, setLng, setGeom, setType, setIidx } = useReportContext();

  // WKT(MULTIPOLYGON) -> [ [lng, lat], ... ] 로 변환
  const parseWktToCoords = (wkt?: string): [number, number][] => {
    if (!wkt || typeof wkt !== 'string') return [];
    // "MULTIPOLYGON(((lng lat,...)))" → "lng lat,..."
    const cleaned = wkt
      .replace(/^MULTIPOLYGON\s*\(\(\(/i, '')
      .replace(/\)\)\)\s*$/i, '');

    // 쉼표로 분리 → "lng lat"
    return cleaned.split(',').map((seg) => {
      const [lngStr, latStr] = seg.trim().split(/\s+/);
      const lng = Number(lngStr);
      const lat = Number(latStr);
      return [lng, lat];
    });
  };

  const getLandAndBuildingInfo = async (params: {
    address: string;
    iidx?: string;
  }) => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiHost}stat/info`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      const data = response.data?.data ?? {};
      const landArray = data.landInfo ?? [];
      const houseResult = data.houseInfo?.result ?? [];
      const houseResult2 = data.houseInfo?.result2 ?? [];
      const map = Array.isArray(data.mapInfo) ? data.mapInfo[0] : null;

      const formatDate = (dateStr?: string | number) => {
        if (!dateStr) return '-';
        const str = String(dateStr);
        if (str.length !== 8) return str;
        return `${str.slice(0, 4)}.${str.slice(4, 6)}.${str.slice(6, 8)}`;
      };

      if (map) {
        const latNum =
          typeof map.lat === 'number' ? map.lat : Number(map.lat) || null;
        const lngNum =
          typeof map.lng === 'number' ? map.lng : Number(map.lng) || null;
        const geomArr = parseWktToCoords(map.geom);

        if (latNum != null) setLat(latNum);
        if (lngNum != null) setLng(lngNum);
        setGeom(geomArr);
      } else {
        // 기본값 초기화
        setGeom([]);
      }

      // 토지 정보
      const landItems: SimpleItem[] = landArray.length
        ? landArray.flatMap((item: any) => [
            {
              label: '면적',
              value: `${item.area ?? '-'}㎡ (${item.pyeong?.toFixed(2) ?? '-'}평)`,
            },
            { label: '용도지역', value: item.zoning_backup ?? '-' },
            { label: '이용상황', value: item.usestatus ?? '-' },
            { label: '지목', value: item.category ?? '-' },
            { label: '용적률', value: `${item.far_rate ?? '-'}%` },
            { label: '건폐율', value: `${item.bcr_rate ?? '-'}%` },
            { label: '고저', value: item.height ?? '-' },
            { label: '도로', value: item.roadsurface ?? '-' },
          ])
        : [];

      // 건물 정보
      const buildingItems: SimpleItem[] =
        houseResult.length || houseResult2.length
          ? (() => {
              const building = { ...houseResult[0], ...houseResult2[0] };
              return [
                { label: '건물명', value: building.buildingname ?? '-' },
                {
                  label: '주 용도',
                  value: building.mainpurposecodename ?? '-',
                },
                { label: '기타 용도', value: building.otheruses ?? '-' },
                {
                  label: '층수',
                  value: `${(building.groundfloornum ?? 0) + (building.basementfloornum ?? 0)}층`,
                },
                { label: '세대 수', value: building.numofhouseholds ?? '없음' },
                {
                  label: '연면적',
                  value: `${building.totalfloorarea ?? building.totalfloorareawobase ?? '-'}㎡ (${building.totalfloorpyeong ?? '-'}평)`,
                },
                {
                  label: '용적률',
                  value: `${building.floorarearatio ?? '-'}%`,
                },
                {
                  label: '건폐율',
                  value: `${building.buildingtolandratio ?? '-'}%`,
                },
                {
                  label: '노후년도',
                  value: building.created_year
                    ? `${building.created_year}년 (노후 ${building.old_year ?? '-'}년)`
                    : `노후 ${building.old_year ?? '-'}년`,
                },
                {
                  label: '사용승인일',
                  value: formatDate(building.approvaldate),
                },
              ];
            })()
          : [];

      setLandItems(landItems);
      setBuildingItems(buildingItems);
    } catch (error) {
      console.error('토지/건물 정보 조회 실패', error);
      setLandItems([]);
      setBuildingItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeOption = async (val: SearchResult) => {
    setSearch(val);
    if (onResult) onResult(val);

    setType('a'); // 검색 결과 성격에 따라 'a'/'p' 등 설정

    if (typeof val.lat === 'number' && typeof val.lng === 'number') {
      setLat(val.lat);
      setLng(val.lng);
      setGeom([]); // 폴리곤은 /stat/info에서 반영
    }

    if (val.address) {
      try {
        setLoading(true);
        const params: any = { address: val.address };
        if (val.caseNumber) {
          const parts = val.caseNumber.split('타경');
          if (parts.length > 1 && parts[1]) {
            params.iidx = parseInt(parts[1], 10);
          }
        }

        await getLandAndBuildingInfo(params);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const initial = {
      name: '다세대/빌라',
      caseNumber: '2022타경101343',
      address: '서울특별시 강남구 개포동 1195-8',
      lat: 37.47462416801767,
      lng: 127.04857137344868,
    };
    handleChangeOption(initial);
  }, []);

  return (
    <Container>
      <SearchButton
        onClick={() => {
          setIsModalOpen(true);
        }}
        disabled={!isPremium}
      >
        주소, 사건번호 검색
        <img src="/assets/images/icon/search-gray.svg" alt="물건 검색" />
      </SearchButton>
      <CaseInfo>
        {loading ? (
          <s.LoaderWrap>
            <s.LoaderImg src="/assets/images/icon/loader.gif" alt="사건번호" />
          </s.LoaderWrap>
        ) : (
          <>
            <CaseInfoInner>
              <CaseType>{search?.name}</CaseType>
              <CaseNumber>{search?.caseNumber}</CaseNumber>
            </CaseInfoInner>

            <CaseAddress>{search?.address}</CaseAddress>
          </>
        )}
      </CaseInfo>
      <SearchModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        value={search}
        onChangeOption={handleChangeOption}
      />
      {!loading && (
        <>
          <MiddleSection>
            <InfoSection>
              <h4>위치</h4>
              <CaseSearchMap groupCategory={search?.name ?? '기타'} />
            </InfoSection>

            {(landItems.length > 0 || buildingItems.length > 0) && (
              <div style={{ marginTop: '16px', width: '100%' }}>
                <s.TabRow style={{ width: '100%' }}>
                  {landItems.length > 0 && (
                    <div style={{ flex: 1 }}>
                      <s.TabPill
                        key="land"
                        $active={selectedTab === 'land'}
                        onClick={() => setSelectedTab('land')}
                        style={{ width: '100%' }}
                      >
                        토지 정보
                      </s.TabPill>
                    </div>
                  )}
                  {buildingItems.length > 0 && (
                    <div style={{ flex: 1 }}>
                      <s.TabPill
                        key="building"
                        $active={selectedTab === 'building'}
                        onClick={() => setSelectedTab('building')}
                        style={{ width: '100%' }}
                      >
                        건물 정보
                      </s.TabPill>
                    </div>
                  )}
                </s.TabRow>
              </div>
            )}

            {selectedTab === 'land' && landItems.length > 0 && (
              <InfoSection>
                <SimpleInfoTable items={landItems} />
              </InfoSection>
            )}

            {selectedTab === 'building' && buildingItems.length > 0 && (
              <InfoSection>
                <SimpleInfoTable items={buildingItems} />
              </InfoSection>
            )}
          </MiddleSection>
        </>
      )}
    </Container>
  );
};

export default CaseSearch;

const Container = styled.div`
  width: 100%;
  background-color: #fff;
  padding: 16px;
  gap: 16px;
  position: relative;
  margin-bottom: 8px;
`;

const CaseInfo = styled.div`
  width: 100%;
  min-height: 88px;
  background-color: #f4f5f7;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  padding: 0 20px;
  flex-direction: column;
  gap: 8px;
`;

const CaseInfoInner = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const CaseType = styled.span`
  min-width: 52px;
  height: 24px;
  color: #fff;
  background: #5b616e;
  padding: 0 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  font-weight: 700;
  font-style: Bold;
  font-size: 13px;
  line-height: 20px;
  letter-spacing: -2%;
  vertical-align: middle;
`;

const CaseNumber = styled.span`
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -2%;
`;

const CaseAddress = styled.div`
  font-weight: 500;
  font-size: 15px;
  line-height: 22px;
  letter-spacing: -2%;
  vertical-align: middle;
  white-space: normal;
  word-break: break-word;
`;

const SearchButton = styled.button`
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  letter-spacing: -2%;
  width: 100%;
  height: 48px;
  border: 1px solid #e8e9ed;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #f4f5f7;
  cursor: pointer;
  margin: 16px 0;

  img {
    width: 20px;
    height: 20px;
  }
`;

const MiddleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  padding-bottom: 40px;
`;

const InfoSection = styled.div`
  h4 {
    margin-bottom: 8px;
  }
`;
